import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Key, UserCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export default function Auth() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  const [step, setStep] = useState(1);
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accessCode.trim()) {
      toast({
        variant: "destructive",
        title: "Access Code Required",
        description: "Please enter an access code to continue.",
      });
      return;
    }

    setLoading(true);
    try {
      const apiBase = (import.meta as any)?.env?.VITE_API_URL || "";
      const response = await fetch(`${apiBase}/api/auth/validate-access-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessCode: accessCode.trim() }),
      });

      const data = await response.json();
      if (response.ok && data.ok) {
        // Auto-fill DEV if access code is DEV
        if (accessCode.toUpperCase() === "DEV") {
          setUsername("DEV");
        }
        setStep(2);
      } else {
        toast({
          variant: "destructive",
          title: "Access Code Error",
          description: data.error || "Invalid access code.",
        });
      }
    } catch (error: any) {
      console.error('[ACCESS_CODE] Validation error:', error);
      toast({
        variant: "destructive",
        title: "Connection Error",
        description: "Cannot reach the server to validate access code. Please check that the app is running.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const apiBase = (import.meta as any)?.env?.VITE_API_URL || "";

      console.log('[LOGIN] Attempting login for:', username, 'with code:', accessCode);

      const response = await fetch(`${apiBase}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: username,   // backend expects email or username
          password: password,
          accessCode: accessCode.toUpperCase() !== "DEV" ? accessCode : undefined,
        }),
      });

      console.log('[LOGIN] Response status:', response.status);
      const data = await response.json();
      console.log('[LOGIN] Response data:', data);

      if (response.ok) {
        toast({
          title: "Login Successful",
          description: `Welcome ${data.user?.fullName || username}`,
        });

        await refreshUser();

        setTimeout(() => {
          if (accessCode.toUpperCase() === "DEV") {
            navigate("/developer-mode");
          } else {
            navigate("/dashboard");
          }
        }, 200);

      } else {
        console.error('[LOGIN] Server rejected login:', data.error);
        toast({
          variant: "destructive",
          title: "Login Failed",
          description: data.error || "Invalid credentials",
        });
      }
    } catch (error: any) {
      console.error('[LOGIN] Network/fetch error:', error);
      toast({
        variant: "destructive",
        title: "Connection Error",
        description: "Cannot reach the server. Please check that the app is running.",
      });
    } finally {
      setLoading(false);
    }
  };


  const getRoleName = () => {
    if (accessCode.toUpperCase() === "DEV") return "System Developer";
    if (accessCode.toUpperCase() === "ADMIN") return "System Administrator";
    if (username.toLowerCase().includes("admin")) return "Pump Administrator";
    return "Pump Operator";
  };

  const formatCode = (code: string) => code.toUpperCase();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3f4f6] p-4 font-sans">
      <Card className="w-full max-w-[400px] shadow-2xl border-0 overflow-hidden flex flex-col bg-white rounded-2xl">
        
        {step === 1 ? (
          <>
            {/* Top Blue Header */}
            <div className="bg-[#1d5ef8] pt-10 pb-8 px-8 flex flex-col items-center justify-center text-white text-center">
              <Key className="h-10 w-10 mb-4 opacity-90 stroke-[2.5]" style={{ transform: "rotate(45deg)" }} />
              <h1 className="text-[26px] font-black tracking-tight mb-1 font-sans">PetroOne</h1>
              <p className="text-blue-100 text-[11px] font-bold uppercase tracking-[0.2em] opacity-80">Fuel Station Management System</p>
            </div>

            {/* Bottom White Form */}
            <div className="p-8">
              <form onSubmit={handleContinue} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[13px] font-bold text-[#1f2937] uppercase tracking-wide">
                    Enter Access Code
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g. RAM4821"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    className="h-12 border-[#d1d5db] focus:ring-2 focus:ring-[#1d5ef8] focus:border-[#1d5ef8] text-base font-medium rounded-xl transition-all shadow-sm"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#1d5ef8] hover:bg-blue-700 text-white font-semibold text-base transition-colors rounded-xl flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  {loading ? "Validating..." : "Continue to Login"} <ArrowRight className="h-5 w-5" />
                </Button>
              </form>
            </div>
          </>
        ) : (
          <>
            {/* Top Navy Header */}
            <div className="bg-[#2f2b6e] relative pt-10 pb-8 px-8 flex flex-col items-center justify-center text-white text-center">
              <button 
                onClick={() => setStep(1)}
                className="absolute top-6 left-6 text-white hover:text-white/80 transition-colors"
                type="button"
              >
                <ArrowRight className="h-5 w-5 rotate-180" />
              </button>
              
              <UserCircle className="h-12 w-12 mb-4 opacity-90 stroke-[1.5]" />
              <p className="text-[13px] font-bold tracking-[0.15em] mb-1.5 opacity-90 text-white uppercase">Accessing Pump:</p>
              <h2 className="text-[26px] font-extrabold tracking-tight mb-4">{getRoleName()}</h2>
              
              <div className="bg-white/10 px-4 py-1 rounded-full border border-white/10 text-xs font-bold tracking-wider">
                CODE: {formatCode(accessCode)}
              </div>
            </div>

            {/* Bottom White Form */}
            <div className="p-8">
              <form onSubmit={handleLogin} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-[13px] font-extrabold text-[#374151] uppercase tracking-widest pl-1">
                    Username
                  </label>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-12 border-[#d1d5db] focus:ring-2 focus:ring-[#2f2b6e] focus:border-[#2f2b6e] text-base font-semibold rounded-xl transition-all shadow-sm px-4"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[13px] font-extrabold text-[#374151] uppercase tracking-widest pl-1">
                    Password
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 border-[#d1d5db] focus:ring-2 focus:ring-[#059669] focus:border-[#059669] text-base font-black tracking-widest rounded-xl transition-all shadow-sm px-4 pr-12"
                      required
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-3.5 text-gray-400 hover:text-gray-600 focus:outline-none"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 mt-4 bg-[#2f2b6e] hover:bg-[#232057] text-white font-bold text-base transition-colors rounded-xl shadow-lg hover:shadow-xl"
                  disabled={loading}
                >
                  {loading ? "Authenticating..." : "Sign In"}
                </Button>
              </form>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}